import { useEffect, useState, useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import PostJob from './PostJob';
import styles from './ManageJob.module.css';
import ProfileSideBar from '../../components/UI/Common/ProfileSideBar/ProfileSideBar';
import EmployerPortalJobList from '../../components/UI/Common/EmployerPortalJobList/EmployerPortalJobList';
import EmployerPortalCandidates from '../../components/UI/Common/EmployerPortalCandidates/EmployerPortalCandidates';
import EmployerPortalApplication from '../../components/UI/Common/EmployerPortalApplications/EmployerPortalApplications';
import {
    ApplicationIcon,
    Briefcase,
    CandidateIcon
} from '../../assets/images/icons';

function ManageJob() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState(0);
    const [activeSteps, setActiveSteps] = useState(1);
    const [postJobEnable, setPostJobEnable] = useState(false)
    const location = useLocation();
    const navigate = useNavigate();

    // Pre-render all tab components but only show the active one
    // This improves tab switching performance
    const [componentsLoaded, setComponentsLoaded] = useState({
        jobs: false,
        candidates: false,
        applications: false
    });

    useEffect(() => {
        // Determine active tab based on URL and update state
        if (location.pathname === '/employer-portal') {
            setActiveTab(0);
            setComponentsLoaded(prev => ({ ...prev, jobs: true }));
        }
        else if (location.pathname === '/employer-portal/jobs') {
            setActiveTab(0);
            setComponentsLoaded(prev => ({ ...prev, jobs: true }));
        }
        else if (location.pathname === '/employer-portal/candidates') {
            setActiveTab(1);
            setComponentsLoaded(prev => ({ ...prev, candidates: true }));
        }
        else if (location.pathname === '/employer-portal/applicants') {
            setActiveTab(2);
            setComponentsLoaded(prev => ({ ...prev, applications: true }));
        }
        else if (location.pathname === '/employer-portal/create-job') {
            setActiveTab(0);
            setPostJobEnable(true);
            setComponentsLoaded(prev => ({ ...prev, jobs: true }));
        }
    }, [location.pathname]);

    const tabs = [
        { name: 'jobs', icon: Briefcase },
        { name: 'candidates', icon: CandidateIcon },
        { name: 'applications', icon: ApplicationIcon },
    ];

    useEffect(() => {
        if (postJobEnable) { navigate('/employer-portal/create-job') }
    }, [postJobEnable, navigate]);

    // Memoize components to prevent unnecessary re-renders
    const jobsComponent = useMemo(() =>
        postJobEnable ?
            <PostJob
                activeTab={activeTab}
                activeSteps={activeSteps}
                setActiveSteps={setActiveSteps}
                setPostJobEnable={setPostJobEnable} /> :
            <EmployerPortalJobList setPostJobEnable={setPostJobEnable} />,
        [activeTab, activeSteps, postJobEnable]
    );

    const candidatesComponent = useMemo(() =>
        <EmployerPortalCandidates setPostJobEnable={setPostJobEnable} />,
        []
    );

    const applicationsComponent = useMemo(() =>
        <EmployerPortalApplication setPostJobEnable={setPostJobEnable} />,
        []
    );

    return (
        <>
            {id &&
                location.pathname !== "/employer-portal/" + id ?
                (
                    <>
                        <Helmet>
                            <title>FindCV - Edit Job</title>
                        </Helmet>
                        <div className={`${styles.container} ${styles.fcvRow} ${postJobEnable ? styles.borderDisabled : styles.borderEnabled}`}>
                            <span className={`${styles.spanStyle} ${postJobEnable ? styles.borderTopBottomEnabled : styles.borderTopBottomDisabled}`}>
                                <ProfileSideBar
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    employerPortal
                                    setActiveTab={setActiveTab}
                                    removeSubscriptionInformation
                                    title={t('forms.manage_job.employer_Portal')}
                                    description={t('forms.manage_job.manage_your_jobs_and_candidates')}
                                />
                            </span>
                            {activeTab === 0 &&
                                <PostJob
                                    activeTab={activeTab}
                                    activeSteps={activeSteps}
                                    id={id}
                                    setActiveSteps={setActiveSteps}
                                    setPostJobEnable={setPostJobEnable} />
                            }
                        </div>
                    </>
                ) : (
                    <>
                        <Helmet>
                            <title>FindCV - Create Job</title>
                        </Helmet>
                        <div className={`${styles.container} ${styles.fcvRow} ${postJobEnable ? styles.borderDisabled : styles.borderEnabled}`}>
                            <span className={`${styles.spanStyle} ${postJobEnable ? styles.borderTopBottomEnabled : styles.borderTopBottomDisabled}`}>
                                <ProfileSideBar
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                    removeSubscriptionInformation
                                    employerPortal
                                    title={t('forms.manage_job.employer_Portal')}
                                    description={t('forms.manage_job.manage_your_jobs_and_candidates')}
                                />
                            </span>
                            <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                                {activeTab === 0 && componentsLoaded.jobs && jobsComponent}
                                {activeTab === 1 && componentsLoaded.candidates && candidatesComponent}
                                {activeTab === 2 && componentsLoaded.applications && applicationsComponent}
                            </div>
                        </div>
                    </>
                )}
        </>
    );
}

export default ManageJob;